import Box from '@mui/material/Box';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MUISelect, { SelectChangeEvent } from '@mui/material/Select';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { isValidElement, useCallback, useEffect, useMemo } from 'react';
import { Controller, FieldValues } from 'react-hook-form';

import { useQueryParam } from '@/hooks/useQueryParam';
import { useTranslate } from '@/hooks/useTranslate';

import { Option } from '@/types/components/common';

import { getDataTestId } from '@/utils/browser';
import { getObjectFieldByPath } from '@/utils/string';

import { HeaderMobile } from '../../Header/HeaderMobile';
import { Icon } from '../../Icon/Icon';
import { TextInput } from '../TextInput';
import { SelectMobileProps } from './Select.interface';
import {
  getSafeSelectValue,
  SelectHelperText,
  SelectSearchClearButton,
  SelectValue,
} from './SelectShared';
import { useSelectLogic } from './useSelectLogic';

import { FieldArrow, FieldArrowButton } from '../Form.styled';
import {
  DropdownOption,
  Label,
  OptionCheckIcon,
  SelectDrawer,
  SelectDrawerSearchBar,
  SelectGroup,
  SelectGroupName,
  StyledFormControl,
} from './Select.styled';

export function SelectMobile<FormPayload extends FieldValues>({
  options = [],
  control,
  name,
  rules,
  helperText,
  withSearchBar,
  maxSelections,
  onSelectChange,
  drawerTitle,
  drawerHeaderLayoutWidths,
  drawerOptionContainerSx,
  renderOption: renderOptionProp,
  renderSelectedOption,
  keepGroupsWhenSearching = false,
  allowClearSearchText = false,
  ...props
}: SelectMobileProps<FormPayload>) {
  const { t } = useTranslate();
  const [selectDrawerQueryValue, setSelectDrawerQueryValue] = useQueryParam(
    `${String(name)}-selectDrawer`,
  );
  const {
    search,
    setSearch,
    isOpen,
    hide,
    show,
    optionByValue,
    groups,
    onSearchChange,
  } = useSelectLogic(options);

  useEffect(() => {
    const onMobileBackPress = () => hide();
    globalThis.addEventListener('popstate', onMobileBackPress);

    return () => globalThis.removeEventListener('popstate', onMobileBackPress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const currentValue = selectDrawerQueryValue;

    if (isOpen) {
      setSearch('');

      if (currentValue !== 'open') {
        setSelectDrawerQueryValue('open');
      }

      return;
    }

    if (currentValue === 'open') {
      setSelectDrawerQueryValue(null, { replace: true });
    }
  }, [isOpen, selectDrawerQueryValue, setSearch, setSelectDrawerQueryValue]);

  const filteredOptions = useMemo(() => {
    const searchValue = search.trim();

    return options.filter(
      item =>
        item.text?.toLowerCase()?.includes(searchValue.toLowerCase()) ||
        String(item.value).toLowerCase().includes(searchValue.toLowerCase()),
    );
  }, [options, search]);

  const filteredGroups = useMemo(() => {
    const searchValue = search.trim();

    if (!searchValue) return groups;

    return groups
      .map(group => ({
        ...group,
        items: group.items.filter(
          item =>
            item.text?.toLowerCase()?.includes(searchValue.toLowerCase()) ||
            String(item.value)
              .toLowerCase()
              .includes(searchValue.toLowerCase()),
        ),
      }))
      .filter(group => group.items.length > 0);
  }, [groups, search]);

  const shouldRenderFlatList =
    groups.length === 1 || (!!search.trim() && !keepGroupsWhenSearching);

  const renderOption = useCallback(
    (
      option: Option,
      currentValue: unknown,
      onSelect: (option: Option) => void,
    ) => {
      const isMultiple = !!props.multiple;
      let selectedValues: unknown[];
      if (Array.isArray(currentValue)) {
        selectedValues = currentValue;
      } else if (currentValue == null) {
        selectedValues = [];
      } else {
        selectedValues = [currentValue];
      }
      const isSelected = selectedValues.includes(option.value);
      const reachedMax =
        isMultiple &&
        typeof maxSelections === 'number' &&
        selectedValues.length >= maxSelections &&
        !isSelected;

      return (
        <DropdownOption
          key={option.value}
          value={option.value}
          disabled={option.disabled || reachedMax}
          onClick={() => onSelect(option)}
          {...getDataTestId(`select-option`)}
        >
          {renderOptionProp ? (
            renderOptionProp(option, { isSelected, reachedMax })
          ) : (
            <>
              {option.icon && (
                <ListItemIcon>
                  <Icon name={option.icon} size={20} />
                </ListItemIcon>
              )}
              <ListItemText>
                <Typography
                  component="span"
                  variant={isSelected ? 'bodyB2Strong' : 'bodyB2Regular'}
                >
                  {option.text || option.value}
                </Typography>
              </ListItemText>
              {isSelected && (
                <OptionCheckIcon>
                  <Icon
                    name="check-mini"
                    color={(theme: Theme) => theme.palette.primaries[500]}
                  />
                </OptionCheckIcon>
              )}
            </>
          )}
        </DropdownOption>
      );
    },
    [maxSelections, props.multiple, renderOptionProp],
  );

  const renderDrawer = useCallback(
    ({
      currentValue,
      onBlur,
      onSelect,
    }: {
      currentValue: unknown;
      onBlur: () => void;
      onSelect: (next: Option | Array<Option['value']>) => void;
    }) => {
      const handleHide = () => {
        hide();
        onBlur();
      };

      const handleSelect = (option: Option) => {
        if (props.multiple) {
          let current: unknown[];
          if (Array.isArray(currentValue)) {
            current = currentValue;
          } else if (currentValue == null) {
            current = [];
          } else {
            current = [currentValue];
          }
          const isSelected = current.includes(option.value);
          let next: unknown[] = [];
          if (isSelected) {
            next = current.filter(v => v !== option.value);
          } else if (
            typeof maxSelections === 'number' &&
            current.length >= maxSelections
          ) {
            next = current;
          } else {
            next = [...current, option.value];
          }
          onSelect(next as Array<Option['value']>);
        } else {
          onSelect(option);
          handleHide();
        }
      };

      const clearSearch = () => setSearch('');

      return (
        <SelectDrawer open={isOpen} onClose={handleHide} anchor="right">
          {withSearchBar && (
            <SelectDrawerSearchBar>
              <TextInput
                placeholder={t('common.search')}
                template="search"
                value={search}
                onChange={onSearchChange}
                autoComplete="off"
                endAdornment={
                  allowClearSearchText && !!search ? (
                    <SelectSearchClearButton onClear={clearSearch} />
                  ) : undefined
                }
              />
            </SelectDrawerSearchBar>
          )}
          <HeaderMobile
            bgColor={theme => theme.palette.background.paper}
            onBack={handleHide}
            layoutWidths={drawerHeaderLayoutWidths}
            centerSlot={
              isValidElement(drawerTitle) && drawerTitle ? (
                drawerTitle
              ) : (
                <Typography variant="bodyB2Regular">
                  {drawerTitle ?? props.label}
                </Typography>
              )
            }
          />
          <Box
            className="options-container"
            width="100%"
            sx={drawerOptionContainerSx}
          >
            {shouldRenderFlatList
              ? filteredOptions.map(item =>
                  renderOption(item, currentValue, handleSelect),
                )
              : filteredGroups.map(group => (
                  <SelectGroup key={group.name} className="group">
                    {group.name && (
                      <SelectGroupName className="group-name">
                        {group.name === 'undefined' ? 'Other' : group.name}
                      </SelectGroupName>
                    )}
                    {group.items.map(item =>
                      renderOption(item, currentValue, handleSelect),
                    )}
                  </SelectGroup>
                ))}
          </Box>
        </SelectDrawer>
      );
    },
    [
      isOpen,
      withSearchBar,
      t,
      search,
      onSearchChange,
      allowClearSearchText,
      setSearch,
      drawerHeaderLayoutWidths,
      drawerTitle,
      props.label,
      props.multiple,
      maxSelections,
      drawerOptionContainerSx,
      shouldRenderFlatList,
      filteredGroups,
      filteredOptions,
      hide,
      renderOption,
    ],
  );

  return (
    <Controller
      rules={rules}
      control={control}
      name={name}
      render={({ field: { onChange, onBlur, value }, formState }) => {
        const defaultFieldValue = getObjectFieldByPath(
          formState.defaultValues,
          name,
        );
        const safeValue = getSafeSelectValue({
          value,
          options,
          isMultiple: props.multiple,
        });
        const safeDefaultValue = getSafeSelectValue({
          value: defaultFieldValue,
          options,
          isMultiple: props.multiple,
        });

        return (
          <>
            <StyledFormControl
              fullWidth={props.fullWidth}
              error={props.error}
              sx={props.sx}
            >
              {props.label && <Label error={props.error}>{props.label}</Label>}
              <MUISelect
                {...getDataTestId(`${name}-select`)}
                {...props}
                value={safeValue}
                fullWidth
                displayEmpty={!!props.placeholder && !props.label}
                defaultValue={safeDefaultValue}
                onClick={props.disabled ? undefined : show}
                open={false}
                multiple={props.multiple}
                IconComponent={() => (
                  <FieldArrowButton>
                    <FieldArrow name={isOpen ? 'arrow-up' : 'arrow-down'} />
                  </FieldArrowButton>
                )}
                renderValue={selected => (
                  <SelectValue
                    value={selected}
                    defaultValue={safeDefaultValue}
                    optionByValue={optionByValue}
                    placeholder={props.placeholder}
                    isMultiple={props.multiple}
                    optionsCount={options.length}
                    renderSelectedOption={renderSelectedOption}
                  />
                )}
              >
                {options.map(option => (
                  <option key={option.value} value={option.value} />
                ))}
              </MUISelect>
              <SelectHelperText helperText={helperText} error={props.error} />
            </StyledFormControl>
            {renderDrawer({
              currentValue: value,
              onSelect: next => {
                const newValue = Array.isArray(next)
                  ? { target: { value: next } }
                  : { target: { value: next.value } };
                onChange(newValue);
                onSelectChange?.(newValue as SelectChangeEvent<unknown>);
              },
              onBlur: onBlur,
            })}
          </>
        );
      }}
    />
  );
}
